import { useState, useEffect } from "react";
import { NavLink, useHistory } from "react-router-dom";
import firebase from "firebase/app";
import { db } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { TripPlan, addDaysISO, readActivities, readDayCount, TRIP_STATUS, tripStatusOf } from "../COMPONENTS/planner/plannerTypes";
import "../CSS/planner.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";

const DEFAULT_DAYS = 3;
const todayISO = () => new Date().toISOString().slice(0, 10);

export const PlannerView = ({ uid, email }: { uid: string; email: string }) => {
  usePageBackground();
  const [plans, setPlans] = useState<TripPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const history = useHistory();

  useEffect(() => {
    const col = db.collection("tripPlans");
    // Firestore can't OR across fields in one query, so fetch owned + shared and
    // merge. allSettled is essential: a rejected shared query must NEVER hide the
    // user's own plans.
    const owned = col.where("uid", "==", uid).get();
    const shared = email
      ? col.where("sharedWith", "array-contains", email.toLowerCase()).get()
      : Promise.resolve(null as any);
    Promise.allSettled([owned, shared]).then(([ownedR, sharedR]) => {
      const byId = new Map<string, TripPlan>();
      const add = (d: any) => {
        if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...(d.data() as Omit<TripPlan, "id">) });
      };
      if (ownedR.status === "fulfilled") ownedR.value.docs.forEach(add);
      if (sharedR.status === "fulfilled" && sharedR.value) sharedR.value.docs.forEach(add);
      const rows = Array.from(byId.values());
      // newest first; createdAt may be missing on brand-new docs
      rows.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
      setPlans(rows);
      setLoading(false);
    });
  }, [uid, email]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const start = todayISO();
      const ref = await db.collection("tripPlans").add({
        uid,
        title: "Ny resa",
        destination: "",
        startDate: start,
        endDate: addDaysISO(start, DEFAULT_DAYS - 1),
        activities: [],
        sharedWith: [],
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      history.push(`${routeUrls.PLANNER}/${ref.id}`);
    } catch {
      setCreating(false);
    }
  };

  const countActivities = (p: TripPlan) => readActivities(p).length;

  const handleDelete = async (e: React.MouseEvent, p: TripPlan) => {
    e.stopPropagation(); // don't open the trip
    if (!window.confirm(`Ta bort "${p.title || "Namnlös resa"}"? Det går inte att ångra.`)) return;
    const prev = plans;
    setPlans((rows) => rows.filter((x) => x.id !== p.id)); // optimistic
    try {
      await db.collection("tripPlans").doc(p.id).delete();
    } catch {
      setPlans(prev); // restore on failure
    }
  };

  return (
    <div className="planner">
      <div className="planner__header">
        <NavLink to={routeUrls.BLOGG} className="planner__back">← Resor</NavLink>
        <h1 className="planner__title">Reseplanerare</h1>
        <button className="planner__new" onClick={handleCreate} disabled={creating}>
          {creating ? "Skapar…" : "+ Ny plan"}
        </button>
      </div>

      {loading && <p className="planner__empty">Laddar…</p>}

      {!loading && plans.length === 0 && (
        <p className="planner__empty">Inga planer ännu. Skapa din första resa!</p>
      )}

      {!loading && plans.length > 0 && (
        <div className="planner__list">
          {plans.map((p) => {
            const st = TRIP_STATUS[tripStatusOf(p.status)];
            return (
            <div
              key={p.id}
              className="planner__plan-card"
              style={{ borderTopColor: st.color }}
              onClick={() => history.push(`${routeUrls.PLANNER}/${p.id}`)}
            >
              <span className="planner__plan-status" style={{ background: st.color }}>
                {st.icon} {st.label}
              </span>
              {p.uid === uid && (
                <button
                  className="planner__plan-del"
                  title="Ta bort resa"
                  onClick={(e) => handleDelete(e, p)}
                >
                  ×
                </button>
              )}
              {p.imageUrl && (
                <img
                  className="planner__plan-img"
                  src={p.imageUrl}
                  alt=""
                  loading="lazy"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              )}
              <div className="planner__plan-body">
                <h2 className="planner__plan-name">
                  {p.title || "Namnlös resa"}
                  {p.uid !== uid && <span className="planner__plan-shared">🤝 Delad</span>}
                </h2>
                {p.destination && <span className="planner__plan-dest">{p.destination}</span>}
                <span className="planner__plan-meta">
                  {readDayCount(p)} dagar · {countActivities(p)} aktiviteter
                </span>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
